import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import Confetti from "@/components/site/Confetti";
import { useMagnetic } from "@/hooks/useMagnetic";
import { useTr } from "@/autoTranslate";
import { useSiteContent } from "@/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const tr = useTr();
  const site = useSiteContent();
  const [form, setForm] = useState({ name: "", email: "", topic: "opportunity", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [burst, setBurst] = useState(0);
  const sendBtnRef = useMagnetic(0.35, 140);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: "err", msg: tr("Lütfen adını, e-postanı ve mesajını doldur.") });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data && res.data.ok) {
        setStatus({
          state: "ok",
          msg: tr("Aldım — bir iki gün içinde durunalan1@gmail.com adresinden yanıt vereceğim."),
        });
        setForm({ name: "", email: "", topic: "opportunity", message: "" });
      } else {
        throw new Error("bad response");
      }
    } catch (err) {
      setStatus({
        state: "err",
        msg: tr("Bir şeyler ters gitti. Tekrar dene ya da doğrudan durunalan1@gmail.com adresine yaz."),
      });
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative px-6 md:px-10 py-8 md:py-12"
    >
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-15%" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="display-sans text-6xl md:text-[10vw] leading-[0.9]"
      >
        {tr(site.get("contact.title"))}
      </motion.h2>

      <div className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-lg md:text-2xl leading-snug max-w-md">
            {tr(site.get("contact.blurb"))}
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={submit}
          className="lg:col-span-7 border border-[color:var(--hair)] rounded-sm p-6 md:p-10 bg-[color:var(--bone-soft)]"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <label className="block">
              <span className="font-mono-label text-[11px] uppercase tracking-widest opacity-70">
                {tr("Adın")}
              </span>
              <input
                data-testid="contact-input-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ada Lovelace"
                className="mt-2 w-full bg-transparent border-b border-[color:var(--ink)] py-2 font-serif-editorial italic text-2xl outline-none focus:border-[color:var(--cobalt)] transition-colors"
              />
            </label>
            <label className="block">
              <span className="font-mono-label text-[11px] uppercase tracking-widest opacity-70">
                {tr("E-posta")}
              </span>
              <input
                data-testid="contact-input-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@studio.co"
                className="mt-2 w-full bg-transparent border-b border-[color:var(--ink)] py-2 font-serif-editorial italic text-2xl outline-none focus:border-[color:var(--cobalt)] transition-colors"
              />
            </label>
          </div>

          <div className="mt-8">
            <span className="font-mono-label text-[11px] uppercase tracking-widest opacity-70">
              {tr("Bu mesaj şununla ilgili")}
            </span>
            <div className="mt-3 flex flex-wrap gap-2">
              {[
                { id: "opportunity", label: "Fırsat" },
                { id: "collab", label: "İş birliği" },
                { id: "press", label: "Basın" },
                { id: "hello", label: "Sadece merhaba" },
              ].map((t) => (
                <button
                  type="button"
                  key={t.id}
                  data-testid={`contact-topic-${t.id}`}
                  onClick={() => setForm({ ...form, topic: t.id })}
                  className={`px-4 py-2 rounded-full border text-sm font-mono-label uppercase tracking-widest transition-all ${
                    form.topic === t.id
                      ? "bg-[color:var(--ink)] text-[color:var(--bone)] border-[color:var(--ink)]"
                      : "border-[color:var(--ink)] text-[color:var(--ink)]"
                  }`}
                >
                  {tr(t.label)}
                </button>
              ))}
            </div>
          </div>

          <label className="block mt-8">
            <span className="font-mono-label text-[11px] uppercase tracking-widest opacity-70">
              {tr("Mesaj")}
            </span>
            <textarea
              data-testid="contact-input-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={5}
              placeholder={tr("Üzerinde çalıştığın şeyi anlat…")}
              className="mt-2 w-full bg-transparent border-b border-[color:var(--ink)] py-2 font-serif-editorial italic text-xl outline-none focus:border-[color:var(--cobalt)] transition-colors resize-none"
            />
          </label>

          <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
            <div
              className={`font-mono-label text-[11px] uppercase tracking-widest ${
                status.state === "err"
                  ? "text-[color:var(--coral)]"
                  : status.state === "ok"
                  ? "text-[color:var(--cobalt)]"
                  : "opacity-70"
              }`}
              data-testid="contact-status"
            >
              {status.state === "idle" && tr("Hazır olduğunda.")}
              {status.state === "loading" && tr("Gönderiliyor…")}
              {status.state === "ok" && status.msg}
              {status.state === "err" && status.msg}
            </div>
            <button
              type="submit"
              ref={sendBtnRef}
              data-testid="contact-submit"
              data-cursor="send"
              data-cursor-label="send"
              disabled={status.state === "loading"}
              className="btn-pill"
            >
              {tr("Gönder")}
              <span aria-hidden>→</span>
            </button>
          </div>
        </form>
      </div>
      <Confetti trigger={burst} />
    </section>
  );
}
